<script lang="ts" setup>
import {
  BlockCarouselImageSelectFields,
  BlockCarouselManualFields,
  BlockCoverAnimationFields,
  BlockCoverNavigationFields,
  BlockCoverPaletteFields,
  BlockCoverUPSFields,
  BlockCoverVariantsFields,
  BlockFAQCollectionFields,
  BlockFooterCTAFields,
  BlockFormFields,
  BlockImageCollectionFields,
  BlockImageTextFields,
  BlockImageWithTextCollectionFields,
  BlockProjectStatsFields,
  BlockQuoteCarouselFields,
  BlockSimpleSpecificationFields,
  BlockSingleQuoteFields,
  BlockVerticalSliderFields,
} from '~/utils/CmsBlockFields'

const { $directus, $readItems, $readItem } = useNuxtApp()

const route = useRoute()

const slug = (route.params.pageSlug as string[]).join('/')

const { data, error } = await useAsyncData(`pages-${slug}`, async () => {
  const projects = await $directus.request($readItems('pages', {
    filter: {
      slug: {
        _eq: slug,
      },
      status: {
        _eq: 'published',
      },
    },
  }))
  if (projects.length === 0) {
    console.error('Page not found')
    throw createError({
      statusCode: 404,
      message: 'no published page with that slug found',
    })
  }

  return await $directus.request($readItem('pages', projects[0]?.id, {
    fields: [
      'id',
      'slug',
      {
        seo: ['*', {
          translations: ['*'],
        }],
        translations: ['*'],
        hero_image: ['*'],
        hero_video: ['*'],

        blocks: [
          'collection',
          'id',
          {
            item: {
              block_carousel_image_select: BlockCarouselImageSelectFields,
              block_carousel_manual: BlockCarouselManualFields,
              block_footer_cta: BlockFooterCTAFields,
              block_image_collection: BlockImageCollectionFields,
              block_image_text: BlockImageTextFields,
              block_cover_animation: BlockCoverAnimationFields,
              block_cover_navigation: BlockCoverNavigationFields,
              block_cover_palette: BlockCoverPaletteFields,
              block_cover_ups: BlockCoverUPSFields,
              block_cover_variants: BlockCoverVariantsFields,
              block_project_stats: BlockProjectStatsFields,
              block_single_quote: BlockSingleQuoteFields,
              block_faq_collection: BlockFAQCollectionFields,
              block_form: BlockFormFields,
              block_image_with_text_collection: BlockImageWithTextCollectionFields,
              block_simple_specification: BlockSimpleSpecificationFields,
              block_vertical_slider: BlockVerticalSliderFields,
              block_quote_carousel: BlockQuoteCarouselFields,
            },
          },
        ],
      },
    ],
  }))
})

if (error.value) {
  // Page is not found in directus, checking if it's a redirect
  // console.log('Page not found, checking for redirect in directus for', `/${slug}`)
  const redirect = await useDirectusRedirect(`/${slug}`)
  if (redirect)
    await navigateTo(redirect, { replace: true /* redirectCode: 301 */ })

  throw createError({
    fatal: true,
    statusCode: 404,
    message: 'Page not found',
  })
}

useDirectusSEO(data.value?.seo)
</script>

<template>
  <div>
    <HeroDetail
      :data="{
        hero_image: data?.hero_image,
        hero_video: data?.hero_video,
        translations: data?.translations,
      } as any
      "
    />
    <div>
      <div v-for="block in data?.blocks" :key="block.id">
        <BlockCarouselImageSelect v-if="block.collection === 'block_carousel_image_select'" :data="block.item" />
        <BlockCarouselManual v-else-if="block.collection === 'block_carousel_manual'" :data="block.item" />
        <BlockFooterCTA v-else-if="block.collection === 'block_footer_cta'" :data="block.item" />
        <BlockImageCollection v-else-if="block.collection === 'block_image_collection'" :data="block.item" />
        <BlockImageText v-else-if="block.collection === 'block_image_text'" :data="block.item" />
        <BlockPoolAnimation v-else-if="block.collection === 'block_cover_animation'" :data="block.item" />
        <BlockPoolNavigation v-else-if="block.collection === 'block_cover_navigation'" />
        <BlockPoolPalette v-else-if="block.collection === 'block_cover_palette'" :data="block.item" />
        <BlockPoolUPS v-else-if="block.collection === 'block_cover_ups'" :data="block.item" />
        <BlockPoolVariants v-else-if="block.collection === 'block_cover_variants'" :specs="data?.tech_specs!" :data="block.item" />
        <BlockProjectStats v-else-if="block.collection === 'block_project_stats'" :data="block.item" />
        <BlockSingleQuote v-else-if="block.collection === 'block_single_quote'" :data="block.item" />
        <BlockFAQCollection v-else-if="block.collection === 'block_faq_collection'" :data="block.item" />
        <BlockForm v-else-if="block.collection === 'block_form'" :data="block.item" />
        <BlockImageWithTextCollection v-else-if="block.collection === 'block_image_with_text_collection'" :data="block.item" />
        <BlockSimpleSpecification v-else-if="block.collection === 'block_simple_specification'" :data="block.item" />
        <BlockVerticalSlider v-else-if="block.collection === 'block_vertical_slider'" :data="block.item" />
        <BlockQuoteCarousel v-else-if="block.collection === 'block_quote_carousel'" :data="block.item" />
      </div>
    </div>
  </div>
</template>

<style>

</style>
